/**
 * Calcola il One Rep Max (1RM) stimato usando la formula di Brzycki
 * @param {number} weight - Peso sollevato
 * @param {number} reps - Ripetizioni eseguite
 * @returns {number} 1RM stimato
 */
export function calculate1RM(weight, reps) {
  // Validazione più robusta dell'input
  if (!weight || weight <= 0 || !reps || reps < 1) return 0;
  if (reps === 1) return weight; // Se è già 1RM, restituisce lo stesso peso
  
  // Formula di Brzycki: 1RM = peso / (1.0278 - 0.0278 × ripetizioni)
  // Aggiungi un limite superiore per ripetizioni (oltre 20 la formula perde accuratezza)
  const effectiveReps = Math.min(reps, 20);
  return weight / (1.0278 - 0.0278 * effectiveReps);
}

/**
 * Verifica se un esercizio è isometrico
 * @param {string} exerciseName - Nome dell'esercizio
 * @returns {boolean} True se l'esercizio è isometrico
 */
export function isIsometricExercise(exerciseName) {
  if (!exerciseName) return false;
  
  const isometricExercises = [
    'plank', 'planc', 'wall sit', 'side plank', 
    'l-sit', 'front lever', 'back lever', 'hollow hold',
    'bridge', 'isometria', 'tenuta', 'superman'
  ];
  
  return isometricExercises.some(exercise => 
    exerciseName.toLowerCase().includes(exercise)
  );
}

/**
 * Raggruppa le serie per esercizio
 * @param {Array} sets - Array di serie di esercizi
 * @returns {Object} Map di esercizi con le relative serie
 */
export function groupSetsByExercise(sets) {
  const exerciseMap = {};

  sets.forEach(set => {
    const exerciseId = set.esercizio_id || set.scheda_esercizio_id;
    const exerciseName = set.esercizio_nome;
    const exerciseType = set.gruppo_muscolare || 'general';
    
    if (!exerciseId || !exerciseName) return;

    if (!exerciseMap[exerciseId]) {
      exerciseMap[exerciseId] = {
        name: exerciseName,
        type: exerciseType,
        sets: []
      };
    }

    exerciseMap[exerciseId].sets.push({
      date: new Date(set.timestamp || set.data || set.data_allenamento || new Date()),
      reps: parseInt(set.ripetizioni) || 0,
      weight: parseFloat(set.peso) || 0
    });
  });

  return exerciseMap;
}

/**
 * Determina la percentuale di incremento basata sul peso attuale
 * @param {number} currentWeight - Peso attuale 
 * @returns {Object} Percentuali di incremento minimo e massimo
 */
function determineIncrementPercentage(currentWeight, progressHistory = null) {
  // Versione base
  if (currentWeight < 5) {
    return { min: 10, max: 15 }; // 10-15% per pesi leggeri
  } else if (currentWeight < 20) {
    return { min: 5, max: 10 }; // 5-10% per pesi medi
  } else if (currentWeight < 50) {
    return { min: 2.5, max: 5 }; // 2.5-5% per pesi più pesanti
  } else {
    return { min: 2, max: 4 }; // 2-4% per pesi molto pesanti
  }
}

/**
 * Regola la percentuale di incremento in base al tipo di esercizio
 * @param {Object} incrementPercent - Percentuali di incremento
 * @param {string} exerciseType - Tipo di esercizio
 * @returns {Object} Percentuali di incremento aggiornate
 */
function adjustIncrementByExerciseType(incrementPercent, exerciseType) {
  const result = { ...incrementPercent };
  
  if (exerciseType.toLowerCase().includes('petto') || 
      exerciseType.toLowerCase().includes('schiena') || 
      exerciseType.toLowerCase().includes('gambe')) {
    // Per esercizi composti grandi muscoli, aumentare un po' di più
    result.min *= 1.2;
    result.max *= 1.2;
  } else if (exerciseType.toLowerCase().includes('spalle') || 
           exerciseType.toLowerCase().includes('bicipiti') ||  
           exerciseType.toLowerCase().includes('avambracci') || 
           exerciseType.toLowerCase().includes('tricipiti')) {
    // Per muscoli più piccoli, incrementi più conservativi
    result.min *= 0.9;
    result.max *= 0.9;
  }
  
  return result;
}

/**
 * Calcola un range di peso consigliato con logica avanzata
 * @param {number} currentWeight - Peso attuale dell'esercizio
 * @param {string} exerciseType - Tipo di esercizio per personalizzazione
 * @param {Object} options - Opzioni aggiuntive per personalizzazione
 * @returns {Object} Range di peso consigliato con dettagli
 */
export function calculateWeightRange(currentWeight, exerciseType = 'general', options = {}) {
  // Validazione input
  if (currentWeight <= 0) {
    return { 
      minWeight: 1, 
      maxWeight: 2, 
      formatted: '1.0 - 2.0 kg',
      rawMin: 1,
      rawMax: 2
    };
  }

  // Configurazione base degli incrementi
  const baseConfig = {
    'general': { min: 2.5, max: 5 },
    'compound': { min: 5, max: 7.5 },  // Esercizi multi-articolari
    'isolation': { min: 1, max: 2.5 }  // Esercizi per singolo muscolo
  };

  // Determina configurazione base
  const config = baseConfig[exerciseType] || baseConfig['general'];

  // Fattori di incremento basati sul peso attuale
  let incrementPercent = determineIncrementPercentage(currentWeight);

  // Adatta l'incremento in base al tipo di esercizio
  incrementPercent = adjustIncrementByExerciseType(incrementPercent, exerciseType);

  // Calcola incrementi
  const minIncrease = Math.max(
    (currentWeight * incrementPercent.min) / 100, 
    config.min
  );
  const maxIncrease = Math.max(
    (currentWeight * incrementPercent.max) / 100, 
    config.max
  );

  // Calcola nuovi pesi
  const minWeight = currentWeight + minIncrease;
  const maxWeight = currentWeight + maxIncrease;

  // Definisci precisione del round basata sul peso
  const roundTo = currentWeight < 10 ? 0.5 : 1;
  
  // Arrotonda i pesi
  const roundedMinWeight = Math.round(minWeight / roundTo) * roundTo;
  const roundedMaxWeight = Math.round(maxWeight / roundTo) * roundTo;

  // Formatta i pesi
  const formatWeight = (weight) => {
    return weight < 10 ? weight.toFixed(1) : weight.toFixed(0);
  };

  // Crea oggetto di ritorno
  return {
    minWeight: roundedMinWeight,
    maxWeight: roundedMaxWeight,
    formatted: `${formatWeight(roundedMinWeight)} - ${formatWeight(roundedMaxWeight)} kg`,
    rawMin: roundedMinWeight,
    rawMax: roundedMaxWeight
  };
}

/**
 * Calcola statistiche per una sessione di allenamento (1RM, peso massimo, peso medio)
 * @param {Array} sessionSets - Array di serie in una sessione
 * @returns {Object} Statistiche della sessione
 */
export function calculateSessionStats(sessionSets) {
  if (!sessionSets || sessionSets.length === 0) {
    return {
      maxWeight: 0,
      averageWeight: 0,
      estimatedRM: 0,
      volume: 0
    };
  }
  
  // Calcola il 1RM per ogni set
  const rmValues = sessionSets.map(set => calculate1RM(set.weight, set.reps));
  
  // Trova il peso massimo sollevato
  const maxWeight = Math.max(...sessionSets.map(set => set.weight));
  
  // Calcola la media ponderata
  const totalWeight = sessionSets.reduce((sum, set) => sum + (set.weight * set.reps), 0);
  const totalReps = sessionSets.reduce((sum, set) => sum + set.reps, 0);
  const averageWeight = totalReps > 0 ? totalWeight / totalReps : 0;
  
  // Trova il 1RM stimato più alto
  const estimatedRM = Math.max(...rmValues);
  
  // Calcola il volume totale (peso × ripetizioni)
  const volume = totalWeight;
  
  return {
    maxWeight,
    averageWeight,
    estimatedRM,
    volume
  };
}